import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import classnames from 'classnames';
import { randomId, sanitizeHtml } from 'javascripts/utils';

const isImmutable = item =>
  Immutable.Map.isMap(item) || Immutable.List.isList(item);

const propTypes = {
  buttonProps: PropTypes.object,
  selectedElements: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({ push: PropTypes.func }),
  ]).isRequired,
  fetchUrl: PropTypes.string,
  fetchOptions: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.shape({ push: PropTypes.func }),
    PropTypes.object,
    PropTypes.shape({ merge: PropTypes.func }),
  ]),
  fetchTransformCallback: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.string,
  ]),
  multiple: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]).isRequired,
  prescript: PropTypes.string,
  nil: PropTypes.node,
  placeholder: PropTypes.string,
  defaultLabel: PropTypes.string,
  headersClickable: PropTypes.bool,
  select: PropTypes.func.isRequired,
  unselect: PropTypes.func,
  clear: PropTypes.func,
  onClose: PropTypes.func,
  classes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  modalClassName: PropTypes.string,
  setButtonRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  resetValueOnOpen: PropTypes.bool,
  nocache: PropTypes.bool,
  uuid: PropTypes.string,
  recentOptions: PropTypes.number,
  recentLabel: PropTypes.string,
  submenuChildren: PropTypes.bool,
  submenuAutoupdate: PropTypes.shape({
    callback: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    selections: PropTypes.array,
    text: PropTypes.shape({
      parent: PropTypes.string,
      child: PropTypes.string,
    }),
  }),
  headerLinks: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      dangerouslySetInnerHtml: PropTypes.string,
      onClick: PropTypes.func.isRequired,
    })
  ),
  headers: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  showSearch: PropTypes.bool,
  footers: PropTypes.arrayOf(PropTypes.string),
  footersReact: PropTypes.any,
  triggerContent: PropTypes.string,
};

/**
 * @extends {React.Component<import('prop-types').InferProps<typeof propTypes>>}
 */
export default class Selectro extends Component {
  static propTypes = propTypes;

  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
    };
  }

  componentDidMount() {
    window.addEventListener('message', this.handleMessage);
  }

  componentWillUnmount() {
    // Ensure the Selectro popup gets closed if it's open.
    this.close();
    window.removeEventListener('message', this.handleMessage);
  }

  close = () => {
    const modal = document.getElementById('selectro-modal');
    // Only allow selectros that have been opened by the user to remove the top level modal.
    // This prevents arbitrary selectro unmounts from closing the modal
    if (modal && this.state.isOpen) {
      modal.__reactRoot?.unmount();
      modal.parentNode?.removeChild(modal);
    }
  };

  handleMessage = event => {
    // When selectro is used in an iframe (e.g. in bookmarks in a portal)
    // the portal will send a message to the iframe to close the selectro
    // https://big.aha.io/develop/features/AI-1679
    if (event.data.eventName === 'ahaSelectro:close') {
      this.close();
    }
  };

  handleClick = async event => {
    const {
      fetchUrl,
      fetchTransformCallback,
      multiple,
      headersClickable,
      prescript,
      nil,
      placeholder,
      showSearch,
      nocache,
      uuid,
      recentOptions,
      recentLabel,
      submenuChildren,
      submenuAutoupdate,
      footers,
      footersReact,
    } = this.props;
    let { selectedElements, fetchOptions } = this.props;
    if (isImmutable(selectedElements)) {
      selectedElements = selectedElements.toJSON();
    }
    if (isImmutable(fetchOptions)) {
      fetchOptions = fetchOptions.toJSON();
    }

    // the event can be used asynchronously when triggered from a componentDidMount call so we persist it
    event.persist();

    event.target.removeAttribute('data-options');
    if (this.props.resetValueOnOpen) {
      event.target.removeAttribute('data-selectrovalue');
    }

    if (typeof fetchOptions === 'function') {
      fetchOptions = await fetchOptions();
    }

    const opts = {
      value: selectedElements,
      fetch: fetchUrl,
      options: fetchOptions,
      fetchTransformCallback,
      multiple,
      prescript,
      nil,
      show_search: showSearch,
      placeholder,
      headers_clickable: headersClickable,
      submenuChildren,
      submenuAutoupdate,
      selectedCallback: ([data]) => {
        // data.option is null when the Clear button is clicked
        if (data.option) {
          // When submenuChildren is true, a selected callback
          // can include multiple options
          const options = Array.isArray(data.option)
            ? data.option
            : Array(data.option);

          options.forEach(option => {
            if (data.action === 'added' || !multiple) {
              this.props.select(option);
            } else {
              this.props.unselect(option);
            }
          });
        } else if (data.action === 'removed') {
          // Unselect all the previously selected options that are no longer selected
          const selectedValues = data.currentState.value.map(
            option => option.value
          );
          data.previousSelected
            .filter(option => !selectedValues.includes(option.value))
            .forEach(option => {
              this.props.unselect(option);
            });
        }
      },
      clearCallback: () => {
        this.props.clear && this.props.clear();
      },
      closeCallback: this.props.onClose,
      nocache: nocache,
      uuid,
      recent_options: recentOptions,
      recent_label: recentLabel,
      footers,
      footersReact,
    };

    // Allow providing header links as can be done on the backend. Selectro
    // needs these to be string HTML so we register a dynamic callback and
    // set the href of the a tag to invoke it.
    if (this.props.headerLinks) {
      if (!window.Callbacks.dynamic) window.Callbacks.dynamic = {};

      opts.headers = this.props.headerLinks.map(link => {
        const clickId = randomId();
        window.Callbacks.dynamic[clickId] = link.onClick;

        const headerTag = document.createElement('a');
        if (link.dangerouslySetInnerHtml) {
          headerTag.innerHTML = sanitizeHtml(link.dangerouslySetInnerHtml);
        } else {
          headerTag.innerText = link.text;
        }
        headerTag.href = `javascript:window.Callbacks.dynamic['${clickId}']()`;
        return headerTag.outerHTML;
      });
    } else if (this.props.headers) {
      opts.headers = this.props.headers;
    }

    this.setState({ isOpen: true });
    window.SelectroHelper.activate(event, opts);
  };

  isValidSelectedElement = element => Array.isArray(element) && element[0];

  // Since selectedElements is a public prop, we need to sanitize it before using it as a label.
  // A valid element must be at least an Array with more than 1 item to avoid malformed labels like `value, `
  // or errors accessing the first item of the element.
  labelFromSelectedElements = () =>
    this.props.selectedElements.reduce((label, element) => {
      const next = this.isValidSelectedElement(element) ? element[0] : '';
      const comma = next && label.length ? ', ' : '';
      return label + comma + `${next}`;
    }, '');

  render() {
    const { disabled, nil, prescript, modalClassName, triggerContent } =
      this.props;
    let { selectedElements } = this.props;

    const selectedLabel = triggerContent || this.labelFromSelectedElements();
    if (isImmutable(selectedElements)) {
      selectedElements = selectedElements.toJSON();
    }

    // If there is one selected element, and the element has meta
    // for a colored box, add the colored box to the trigger.
    let coloredBox = null;
    if (selectedElements.length === 1) {
      let boxConfiguration = selectedElements[0][2]?.meta?.prepend?.[0];
      if (boxConfiguration) {
        coloredBox = (
          <span
            className='selectro-option-text-prepend selectro-colored-box'
            style={{
              backgroundColor: boxConfiguration.colored_box,
              borderColor: boxConfiguration.border_color,
            }}
          />
        );
      }
    }

    return (
      <button
        key={selectedElements}
        disabled={disabled}
        ref={this.props.setButtonRef}
        onClick={this.handleClick}
        className={classnames('selectro-trigger', this.props.classes)}
        data-selectrovalue={JSON.stringify(selectedElements)}
        data-has-custom-trigger-content={Boolean(triggerContent) || null}
        data-modal_class_name={modalClassName}
        {...this.props.buttonProps}
      >
        <div
          className='selectro-trigger-content-wrapper'
          data-prescript={prescript}
        >
          <div className='selectro-trigger-content'>
            <div>
              {coloredBox}
              {selectedLabel || this.props.defaultLabel || nil}
            </div>
          </div>
          <div className='selectro-trigger-inputs' />
        </div>
      </button>
    );
  }
}
